import { zodResolver } from "@hookform/resolvers/zod"
import type * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"
import type { BaseSyntheticEvent } from "react"
import * as React from "react"
import { useId, useMemo } from "react"
import {
  Controller,
  type ControllerProps,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  FormProvider,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormHandleSubmit,
  type UseFormProps as UseReactHookFormProps,
  type UseFormReturn as UseReactHookFormReturn,
  useFormContext,
  useFormState,
  useForm as useReactHookForm,
  useWatch,
} from "react-hook-form"
import type * as z from "zod"

import { Label } from "@repo/ui/components/ui/label"
import { useStableFunction } from "@repo/ui/hooks/use-stable-function"
import { cn } from "@repo/ui/lib/utils"
import type { Prettify } from "@repo/utils/types"

type FormProps = Prettify<
  // biome-ignore lint/suspicious/noExplicitAny: We're narrowing the type when used
  React.ComponentPropsWithoutRef<typeof FormProvider<any>> & {
    id?: string
    noValidate?: boolean
    className?: string
    testId?: string
    onFocus?: React.FocusEventHandler<HTMLFormElement>
    /**
     * Only use if you want to explicitly handle the submit event without using the `useForm` hook.
     * Ideally, you should pass the `onSubmit` function to the `useForm` hook and it will provide you
     * with a wrapped `onSubmit` prop that you can pass to the `Form` component.
     */
    onSubmit?: React.FormEventHandler<HTMLFormElement>
  }
>

const Form: React.ForwardRefExoticComponent<
  FormProps & React.RefAttributes<HTMLFormElement>
> = React.forwardRef<HTMLFormElement, FormProps>(
  (
    {
      children,
      id,
      noValidate = true,
      className,
      onFocus,
      onSubmit,
      testId,
      ...props
    },
    ref,
  ) => {
    return (
      <FormProvider {...props}>
        <form
          ref={ref}
          id={id}
          noValidate={noValidate}
          className={className}
          onFocus={onFocus}
          onSubmit={onSubmit}
          data-testid={testId}
        >
          {children}
        </form>
      </FormProvider>
    )
  },
)
Form.displayName = "Form"

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState } = useFormContext()
  const formState = useFormState({ name: fieldContext.name })
  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
)

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        data-slot="form-item"
        className={cn("grid gap-2", className)}
        {...props}
      />
    </FormItemContext.Provider>
  )
}

function FormLabel({
  className,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId } = useFormField()

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn("data-[error=true]:text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  )
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField()

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

const hasMessage = (x: unknown): x is { message?: unknown } =>
  typeof x === "object" && x !== null && "message" in x

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField()

  let body = props.children

  if (error) {
    if (Array.isArray(error)) {
      body = error
        .map((e) => {
          if (typeof e === "object" && e !== null && !hasMessage(e)) {
            const values = Object.values<object>(e)

            return values
              .map((v) => (hasMessage(v) ? String(v.message) : String(v)))
              .join(", ")
          }

          return String(e.message || "")
        })
        .filter(Boolean)
        .join(", ")
    } else {
      body = String(error.message || "")
    }

    if (!body) {
      body = props.children || "Inputs are not valid"
    }
  }

  if (!body) {
    return null
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn("font-medium text-destructive text-sm", className)}
      {...props}
    >
      {body}
    </p>
  )
}

type UseFormProps<
  Input extends FieldValues = FieldValues,
  // biome-ignore lint/suspicious/noExplicitAny: We're narrowing the type when used
  Context = any,
  Output = Input,
> = Prettify<{
  formId?: string
  formSchema: z.ZodType<Output, Input>
  onSubmit?: SubmitHandler<Input>
  onInvalid?: SubmitErrorHandler<Input>
}> &
  UseReactHookFormProps<Input, Context, Output>

type UseFormReturn<
  Input extends FieldValues = FieldValues,
  // biome-ignore lint/suspicious/noExplicitAny: We're narrowing the type when used
  Context = any,
  Output = Input,
> = Prettify<
  UseReactHookFormReturn<Input, Context, Output> & {
    id: string
    onSubmit: ReturnType<UseFormHandleSubmit<Input, Output>>
  }
>

/**
 * This is a thin wrapper around react-hook-form's useForm hook that adds
 * the ability to pass in a form (zod) schema to validate the form.
 */
const useForm = <
  Input extends FieldValues = FieldValues,
  // biome-ignore lint/suspicious/noExplicitAny: We're narrowing the type when used
  Context = any,
  Output = Input,
>({
  formId,
  formSchema,
  onSubmit,
  onInvalid,
  ...hookProps
}: UseFormProps<Input, Context, Output>): UseFormReturn<
  Input,
  Context,
  Output
> => {
  const id = useId()

  const formMethods = useReactHookForm<Input, Context, Output>({
    resolver: zodResolver(formSchema),
    ...hookProps,
  })

  const onSubmitHandler: SubmitHandler<Output> = useStableFunction(
    async (
      data,
      // biome-ignore lint/suspicious/noExplicitAny: We're narrowing the type when used
      event?: BaseSyntheticEvent<object, any, any>,
    ) => {
      if (onSubmit) {
        await onSubmit(data, event)
      }
    },
  )

  return useMemo(
    () => ({
      ...formMethods,
      id: formId || id,
      onSubmit: formMethods.handleSubmit(onSubmitHandler, onInvalid),
    }),
    [formMethods, onSubmitHandler, onInvalid, formId, id],
  )
}

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormProvider,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type FormProps,
  type UseFormReturn,
  useForm,
  useFormField,
  useWatch,
}
